import { IInvoice } from '$models'
import {
  InvoiceVersion,
  InvoiceStatusFilter,
  invoiceFileStatusOptions,
} from '$constants'
import { invoiceService } from '$services'
import { exceptionMessageHandler } from '$utils'

/**
 * Turns an array of objects that have an property of the key param into a dictionary
 * @param key The property to the object to use and the dictionary key (MUST be unique)
 * @param array Array of objects that have a property of the key param
 * @returns dictionary of the objects keyed on the key param
 */
export function arrayToDictionary<T>(
  key: string,
  array: T[]
): Record<string, T> {
  const dict: Record<string, T> = {}

  array.forEach((item: T) => {
    if (!item[key]) {
      throw new Error(`T[key] cannot be undefined`)
    }

    dict[item[key]] = item
  })

  return dict
}

/**
 * Turns a dictionary of objects into an array.
 * This is commonly used to turn our dictionary of invoiceLines keyed by ID
 * into an array that we can use to render elements.
 * @param dict Dictionary of objects
 * @returns Array of objects
 */
export function dictionaryToArray<T>(dict: Record<string, T>): T[] {
  return Object.values(dict)
}

export const getInvoiceStatus = (invoice: IInvoice) => {
  let status = InvoiceStatusFilter.Failed
  if (
    invoice.version !== InvoiceVersion.Failed &&
    invoice.version !== InvoiceVersion.Processing
  ) {
    status = invoice.isReceived
      ? InvoiceStatusFilter.Received
      : InvoiceStatusFilter.NotReceived
  }
  return invoiceFileStatusOptions.find((s) => s.value === status)
}

/**
 * Opens the file viewer with the invoice file
 * @param fileId The ID of the file to open
 * @param supplierName The supplier name for the file name
 * @param referenceNumber The invoice reference number for the file name
 * @param setIsFileVisible Function to set file viewer visibility
 * @param setData Function to set file viewer data
 */
export async function openInvoiceFileViewer(
  fileId: string,
  supplierName: string | undefined,
  referenceNumber: string | undefined,
  setIsFileVisible: (visible: boolean) => void,
  setData: (data: {
    fileName: string
    fileBlob: Blob
    contentType: string
  }) => void
): Promise<void> {
  try {
    setIsFileVisible(true)
    const response = await invoiceService.getInvoiceFile(fileId)
    const fileBlob = response?.data ?? null
    const contentType = response?.contentType ?? null

    if (fileBlob && contentType) {
      setData({
        fileName: [
          supplierName ?? 'Unknown supplier',
          referenceNumber ?? 'invoice',
        ].join(' - '),
        fileBlob,
        contentType,
      })
    }
  } catch (error) {
    setIsFileVisible(false)
    exceptionMessageHandler(error, 'Failed to open invoice file')
  }
}
